import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../store";
import { useAlerts } from "../hooks/useAlerts";
import { useCountIncrease } from "../hooks/useCountIncrease";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { AlertKind, ClaimAlert, filterAlerts } from "../util/alerts";
import { payerPath, residentPath } from "./GlobalSearch";
import "./AlertsBell.scss";

/**
 * The two icons and their lists for the given alerts: a bell for follow-ups that turned orange and an envelope for
 * messages. The navbar passes every facility's alerts, a dashboard just its own payer's.
 */
export function AlertsMenu({
  alerts,
  refresh,
  loaded,
  push,
  variant = "dark",
  onNavigate,
}: {
  alerts: ClaimAlert[];
  refresh: () => unknown;
  loaded: boolean;
  /** Browser-notification settings; left out where they do not belong */
  push?: ReturnType<typeof usePushNotifications>;
  /** dark: for the navbar, light: for the page */
  variant?: "dark" | "light";
  onNavigate?: () => void;
}) {
  const { user } = useUser();
  const [open, setOpen] = useState<AlertKind | null>(null);
  const [query, setQuery] = useState("");
  const container = useRef<HTMLDivElement>(null);
  const overdue = alerts.filter((alert) => alert.kind === "overdue");
  const messages = alerts.filter((alert) => alert.kind === "message");
  const listed = open === "overdue" ? overdue : messages;
  const shown = filterAlerts(listed, query);
  const newOverdue = useCountIncrease(overdue.length, loaded);
  const newMessages = useCountIncrease(messages.length, loaded);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggle = (kind: AlertKind) => {
    if (open !== kind) {
      refresh();
      (kind === "overdue" ? newOverdue : newMessages).dismiss();
    }
    setQuery("");
    setOpen(open === kind ? null : kind);
  };

  return (
    <div className={clsx("alerts-bell", { "is-light": variant === "light" })} ref={container} onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={clsx("alerts-bell-button is-overdue", { "is-alerting": newOverdue.highlighted })}
        aria-label={`Overdue follow-ups, ${overdue.length}`}
        title={variant === "light" ? "Overdue follow-ups in this payer" : "Overdue follow-ups"}
        aria-expanded={open === "overdue"}
        onClick={() => toggle("overdue")}
      >
        <i className="fa-solid fa-bell" aria-hidden="true" />
        {overdue.length > 0 && (
          <span className={clsx("alerts-bell-badge is-overdue", { "is-new": newOverdue.highlighted })}>{overdue.length}</span>
        )}
      </button>
      <button
        type="button"
        className={clsx("alerts-bell-button is-message", { "is-alerting": newMessages.highlighted })}
        aria-label={`Messages, ${messages.length}`}
        title={variant === "light" ? "Messages in this payer" : "Messages"}
        aria-expanded={open === "message"}
        onClick={() => toggle("message")}
      >
        <i className="fa-solid fa-envelope" aria-hidden="true" />
        {messages.length > 0 && (
          <span className={clsx("alerts-bell-badge", { "is-new": newMessages.highlighted })}>{messages.length}</span>
        )}
      </button>
      {variant === "dark" && (
        <span className="is-sr-only" role="status">
          {newOverdue.highlighted ? "A follow-up is overdue. " : ""}
          {newMessages.highlighted ? "You have new messages." : ""}
        </span>
      )}
      {open && (
        <div key={open} className={clsx("alerts-bell-panel", `is-${open}`)} role="region" aria-label={open === "overdue" ? "Overdue follow-ups" : "Messages"}>
          <div className="alerts-bell-header">
            <span>
              <i className={clsx("fa-solid", open === "overdue" ? "fa-clock" : "fa-envelope")} aria-hidden="true" />{" "}
              {query.trim() ? `${shown.length} of ${listed.length}` : listed.length} {open === "overdue" ? "overdue follow-ups" : "messages"}
            </span>
          </div>
          <div className="alerts-bell-search">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search resident, facility or text…"
              aria-label={open === "overdue" ? "Search overdue follow-ups" : "Search messages"}
              autoComplete="off"
            />
          </div>
          {push && (push.state === "off" || push.state === "on" || push.state === "denied") && (
            <div className="alerts-bell-push">
              {push.state === "denied" ? (
                <span>Browser notifications are blocked in your browser settings.</span>
              ) : (
                <>
                  <span>{push.state === "on" ? "Browser notifications are on." : "Get notified even when this tab is closed."}</span>
                  <button type="button" className="alerts-bell-clear" disabled={push.busy} onClick={push.state === "on" ? push.disable : push.enable}>
                    {push.state === "on" ? "Turn off" : "Turn on"}
                  </button>
                </>
              )}
            </div>
          )}
          {shown.length === 0 ? (
            <p className="alerts-bell-empty">
              {query.trim() ? `Nothing matches “${query.trim()}”.` : open === "overdue" ? "No overdue follow-ups." : "No messages."}
            </p>
          ) : (
            <ul>
              {shown.map((alert) => (
                <li key={alert.id}>
                  <div className={clsx("alerts-bell-item", `is-${alert.kind}`)}>
                    <i className={clsx("fa-solid alerts-bell-icon", alert.kind === "overdue" ? "fa-clock" : "fa-envelope")} aria-hidden="true" />
                    <span className="alerts-bell-body">
                      {/* Stretched over the whole row; the facility link below sits on top of it */}
                      <Link
                        to={residentPath(user.group, alert)}
                        className="alerts-bell-main"
                        onClick={() => {
                          setOpen(null);
                          onNavigate?.();
                        }}
                      >
                        <span className="alerts-bell-title">
                          <strong>{alert.residentName}</strong>
                          <span className="alerts-bell-tag">{alert.kind === "overdue" ? "Overdue" : "Message"}</span>
                        </span>
                        <span className="alerts-bell-what">
                          {alert.kind === "overdue" ? `${alert.author}: ${alert.text}` : `${alert.detail}: ${alert.text}`}
                        </span>
                      </Link>
                      <span className="alerts-bell-where">
                        <Link
                          to={payerPath(user.group, alert)}
                          className="alerts-bell-where-link"
                          onClick={() => {
                            setOpen(null);
                            onNavigate?.();
                          }}
                        >
                          <span className="capitalize">{alert.facility}</span> / <span className="uppercase">{alert.payer}</span>
                        </Link>
                        {" · "}
                        {alert.billType}
                        {alert.kind === "overdue" && <> · Follow-up overdue since {alert.detail}</>}
                      </span>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** The navbar's icons: alerts from every facility and payer of the signed-in user */
export default function AlertsBell({ onNavigate }: { onNavigate?: () => void }) {
  const { alerts, refresh, loaded } = useAlerts();
  const push = usePushNotifications();
  return <AlertsMenu alerts={alerts} refresh={refresh} loaded={loaded} push={push} onNavigate={onNavigate} />;
}
